import { FC, useState } from 'react';
import {
  FormControl,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { FontGroup } from '../utils/fontUtils';
import BrandBadge from '../../BrandBadge';
import { Flex } from '@chakra-ui/react';
import { BrandVariable } from '@adflow/types';

interface Props {
  value: string;
  fonts: FontGroup[];
  customFonts: { fontFamily: string; isDefault?: boolean }[];
  primaryFont?: string | null;
  secondaryFont?: string | null;
  onChange: (family: string) => void;
}

const getFontLabel = (
  family: string,
  customFonts: { fontFamily: string; isDefault?: boolean }[],
  primaryFont?: string | null,
  secondaryFont?: string | null,
) => {
  if ((primaryFont && primaryFont === family) || (secondaryFont && secondaryFont === family))
    return '';

  const font = customFonts.find((f) => f.fontFamily === family);

  if (!font) return ' (Not installed)';

  return font?.isDefault ? ' (Default)' : ' (Custom)';
};

const containsText = (text: string, searchText: string) =>
  text.toLowerCase().includes(searchText.toLowerCase());

export const FontFamilySelect: FC<Props> = ({
  value,
  fonts,
  customFonts,
  primaryFont,
  secondaryFont,
  onChange,
}) => {
  const [searchText, setSearchText] = useState('');

  const displayedFonts = searchText
    ? fonts.filter(({ family }) => containsText(family, searchText))
    : fonts;

  return (
    <FormControl fullWidth>
      <Select
        size="small"
        aria-label="Font family"
        value={value}
        onClose={() => setSearchText('')}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) return <Typography color="text.secondary">Select Font Family</Typography>;
          const label = getFontLabel(selected as string, customFonts, primaryFont, secondaryFont);
          return (
            <Flex alignItems="center" gap={1}>
              <Typography>
                {selected as string} {label}
              </Typography>
              {primaryFont && primaryFont === (selected as string) && (
                <BrandBadge variableType={BrandVariable.PRIMARY_FONT} />
              )}
              {secondaryFont && secondaryFont === (selected as string) && (
                <BrandBadge variableType={BrandVariable.SECONDARY_FONT} />
              )}
            </Flex>
          );
        }}
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            style: {
              maxHeight: '300px',
              overflowY: 'auto',
            },
          },
        }}
      >
        <ListSubheader>
          <TextField
            size="small"
            autoFocus
            placeholder="Search fonts..."
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </ListSubheader>

        {displayedFonts.map(({ family }) => {
          const label = getFontLabel(family, customFonts, primaryFont, secondaryFont);
          return (
            <MenuItem key={family} value={family} onClick={() => onChange(family)}>
              <Flex alignItems="center" gap={1}>
                <Typography>
                  {family} {label}
                </Typography>
                {primaryFont && primaryFont === family && (
                  <BrandBadge variableType={BrandVariable.PRIMARY_FONT} />
                )}
                {secondaryFont && secondaryFont === family && (
                  <BrandBadge variableType={BrandVariable.SECONDARY_FONT} />
                )}
              </Flex>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};
